import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Routes, Route } from 'react-router-dom';
import type { AppDispatch } from './store';
import { tryAutoLogin } from './store/slices/authSlice';

// Import layouts
import MainLayout from './layouts/MainLayout';
import AuthLayout from './layouts/AuthLayout';

// Import pages
import LoginPage from './pages/auth/LoginPage';
import RegistrationPage from './pages/auth/RegistrationPage';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';
import ResetPasswordPage from './pages/auth/ResetPasswordPage';
import VerifyEmailPage from './pages/auth/VerifyEmailPage';
import ProfilePage from './pages/profile/ProfilePage';
import EditProfilePage from './pages/profile/EditProfilePage';
import HomePage from './pages/HomePage';
import HowItWorksPage from './pages/HowItWorksPage';
import NotFoundPage from './pages/NotFoundPage';
import TermsPage from './pages/legal/TermsPage';
import PrivacyPage from './pages/legal/PrivacyPage';
import LeagueListPage from './pages/league/LeagueListPage';
import LeagueDetailPage from './pages/league/LeagueDetailPage';
import CreateLeaguePage from './pages/league/CreateLeaguePage';
import EditLeaguePage from './pages/league/EditLeaguePage';
import ManageSeasonPage from './pages/league/ManageSeasonPage';
import ManageDivisionPage from './pages/league/ManageDivisionPage';
import TeamsIndexRedirect from './pages/team/TeamsIndexRedirect';
import TeamsPage from './pages/team/TeamsPage';
import CreateTeamPage from './pages/team/CreateTeamPage';
import TeamDetailPage from './pages/team/TeamDetailPage';
import ReRegistrationPage from './pages/team/ReRegistrationPage';
import StandingsPage from './pages/league/StandingsPage';
import SeasonTransitionPage from './pages/admin/SeasonTransitionPage';
import AdminPlayersPage from './pages/admin/AdminPlayersPage';
import DivisionsPage from './pages/league/DivisionsPage';
import ManageDivisionsPage from './pages/league/ManageDivisionsPage';
import GameListPage from './pages/game/GameListPage';
import GameDetailPage from './pages/game/GameDetailPage';
import ProposalsRedirect from './pages/game/ProposalsRedirect';
import ProposalDetailPage from './pages/game/ProposalDetailPage';
import GamesPage from './pages/game/GamesPage';
import ProposeGamePage from './pages/game/ProposeGamePage';
import RecordGamePage from './pages/game/RecordGamePage';
import EditGamePage from './pages/game/EditGamePage';
import ResultEntryPage from './pages/game/ResultEntryPage';
import ResultApprovePage from './pages/game/ResultApprovePage';
import ManageDivisionGamesPage from './pages/league/ManageDivisionGamesPage';

// Import routes
import ProtectedRoute from './routes/ProtectedRoute';

const App: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();

  useEffect(() => {
    // Try to auto-login the user with stored refresh token
    dispatch(tryAutoLogin());
  }, [dispatch]);

  return (
    <Routes>
      {/* Auth routes — centered AuthLayout, no sidebar */}
      <Route path="/" element={<AuthLayout />}>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegistrationPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
        <Route path="/verify-email/:token" element={<VerifyEmailPage />} />
      </Route>

      {/* ── Main app routes — all render inside MainLayout ─────────────── */}
      {/*
       * Public-first routing: the MainLayout chrome (header, league banner,
       * sidebar) is always visible. ProtectedRoute only wraps pages that
       * require authentication to VIEW (profile, mutating forms).
       * Read-only pages (home, divisions, teams, games, standings) are public.
       */}
      <Route path="/" element={<MainLayout />}>
        {/* ── Fully public pages ──────────────────────────────────────── */}
        <Route index element={<HomePage />} />

        {/* How it Works — public onboarding page for new/prospective players */}
        <Route path="/how-it-works" element={<HowItWorksPage />} />

        {/* League browse (public read) */}
        <Route path="/leagues" element={<LeagueListPage />} />
        <Route path="/leagues/:leagueId" element={<LeagueDetailPage />} />

        {/* Divisions (public read) — category is an optional slug param. The
            unscoped /divisions falls back to the banner-selected league so the
            sidebar link never lands on the Leagues list. */}
        <Route path="/divisions" element={<DivisionsPage />} />
        <Route path="/leagues/:leagueId/divisions" element={<DivisionsPage />} />
        <Route path="/leagues/:leagueId/divisions/:category" element={<DivisionsPage />} />

        {/* Standings (public read) */}
        <Route
          path="/leagues/:leagueId/seasons/:seasonId/divisions/:divisionId/standings"
          element={<StandingsPage />}
        />

        {/* League-scoped Games page (public read) — resolves sidebar Games link */}
        <Route path="/leagues/:leagueId/games" element={<GamesPage />} />

        {/* Game list + detail (public read) */}
        <Route path="/games" element={<GameListPage />} />
        <Route path="/games/:gameId" element={<GameDetailPage />} />

        {/* Bare /teams → redirect to the league-scoped Teams page */}
        <Route path="/teams" element={<TeamsIndexRedirect />} />

        {/* League-scoped Teams page (public read) */}
        <Route path="/leagues/:leagueId/teams" element={<TeamsPage />} />

        {/* Team detail (public read) */}
        <Route path="/teams/:teamId" element={<TeamDetailPage />} />

        {/* ── Protected: require auth to VIEW ────────────────────────── */}
        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <ProfilePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile/edit"
          element={
            <ProtectedRoute>
              <EditProfilePage />
            </ProtectedRoute>
          }
        />

        {/* ── Protected: mutating team actions ───────────────────────── */}

        {/* Create team — unified entry point (forms the team and enrolls it into
            a division). Replaces the old Join League + Register Review flows. */}
        <Route
          path="/teams/new"
          element={
            <ProtectedRoute>
              <CreateTeamPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/leagues/:leagueId/seasons/:seasonId/re-register"
          element={
            <ProtectedRoute>
              <ReRegistrationPage />
            </ProtectedRoute>
          }
        />

        {/* ── Protected: admin-only pages ────────────────────────────── */}

        {/* Players admin view (League/Super Admin only) */}
        <Route
          path="/leagues/:leagueId/players"
          element={
            <ProtectedRoute>
              <AdminPlayersPage />
            </ProtectedRoute>
          }
        />

        {/* ── Protected: league admin / mutating league actions ───────── */}

        {/* Manage divisions (admin-only) */}
        <Route
          path="/leagues/:leagueId/divisions/:category/manage"
          element={
            <ProtectedRoute>
              <ManageDivisionsPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/leagues/create"
          element={
            <ProtectedRoute>
              <CreateLeaguePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/leagues/:leagueId/edit"
          element={
            <ProtectedRoute>
              <EditLeaguePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/leagues/:leagueId/seasons/create"
          element={
            <ProtectedRoute>
              <ManageSeasonPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/leagues/:leagueId/seasons/:seasonId"
          element={
            <ProtectedRoute>
              <ManageSeasonPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/leagues/:leagueId/seasons/:seasonId/divisions/create"
          element={
            <ProtectedRoute>
              <ManageDivisionPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/leagues/:leagueId/seasons/:seasonId/divisions/:divisionId"
          element={
            <ProtectedRoute>
              <ManageDivisionPage />
            </ProtectedRoute>
          }
        />
        {/* Manage games within a specific division of a season (admin-only) */}
        <Route
          path="/leagues/:leagueId/seasons/:seasonId/divisions/:divisionId/games/manage"
          element={
            <ProtectedRoute>
              <ManageDivisionGamesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/leagues/:leagueId/seasons/:seasonId/transition"
          element={
            <ProtectedRoute>
              <SeasonTransitionPage />
            </ProtectedRoute>
          }
        />

        {/* The standalone proposals list is gone — forward to the Games page's
            Proposals tab. */}
        <Route path="/games/proposals" element={<ProposalsRedirect />} />
        {/* ProposeGamePage — reshaped propose-game flow per propose-game.html */}
        <Route
          path="/games/proposals/create"
          element={
            <ProtectedRoute>
              <ProposeGamePage />
            </ProtectedRoute>
          }
        />
        {/* RecordGamePage — record an already-played game + result in one step */}
        <Route
          path="/games/record"
          element={
            <ProtectedRoute>
              <RecordGamePage />
            </ProtectedRoute>
          }
        />
        {/* EditGamePage — shared admin "Edit a Game" screen (Edit + Resolve entry points) */}
        <Route
          path="/games/:gameId/edit"
          element={
            <ProtectedRoute>
              <EditGamePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/games/proposals/:proposalId"
          element={
            <ProtectedRoute>
              <ProposalDetailPage />
            </ProtectedRoute>
          }
        />

        {/* Result entry (auth required — mutating) */}
        <Route
          path="/games/:gameId/result"
          element={
            <ProtectedRoute>
              <ResultEntryPage />
            </ProtectedRoute>
          }
        />

        {/* Result approval / dispute (auth required — mutating) */}
        <Route
          path="/games/:gameId/approve"
          element={
            <ProtectedRoute>
              <ResultApprovePage />
            </ProtectedRoute>
          }
        />
      </Route>

      {/* Catch-all route for 404 */}
      {/* Standalone legal documents — self-contained layout, readable in any
          auth state (linked from the registration form). */}
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />

      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
};

export default App;
